/**
 * FontSizingFeature - Per-element font size, line spacing, word spacing, letter spacing
 * Mode 'readableFont' switches to a readable font family
 * Scans text elements and adjusts each one individually (like EqualWeb / EasyView)
 */
import BaseFeature from './BaseFeature.js';

export default class FontSizingFeature extends BaseFeature {
    constructor(widget, options = {}) {
        super(widget, options);
        const fsConfig = this.widget.config.featureSettings?.fontSizing || {};

        this.mode = options.mode || null;
        this.settings = {
            min: fsConfig.min || 80,
            max: fsConfig.max || 200,
            step: fsConfig.step || 10
        };
        this.currentSize = 100;
        this.currentLineSpacing = 0;
        this.currentWordSpacing = 0;
        this.currentLetterSpacing = 0;

        // Per-element storage
        this._scanned = false;
        this._elements = [];

        // Selectors to scan (same as EasyView)
        this._tags = ['h1','h2','h3','h4','h5','h6','p','a','span','div','li','strong','em',
                       'td','th','label','button','input','textarea','select','blockquote',
                       'figcaption','caption','dt','dd','pre','code','.btn-primary'];

        // Ignore elements inside these containers - read from TypoScript config
        this._ignoreSelectors = fsConfig.ignoreSelectors || '#accessibility-widget, .accessibility-control-bar, .typo3-adminPanel, #INDWrap, #t3readspeaker-button, .ignoreEasyView, .footer .iconMenu>li>a, .headerTop ul.iconMenu.pull-right li>a, .toggleMobileMenu';

        // Font sizing configuration from TypoScript
        this.presets = (options.presets || {});
        this.maxFontSize = options.maxFontSize || 44;
        this.wordBreak = options.wordBreak || 'break-word';
        this.maxFontWeight = options.maxFontWeight || 700;
        
        // Debug: log preset configuration
        if (this.widget.config.debug) {
            console.log('[A11Y FontSizing] Constructor - presets:', this.presets);
            console.log('[A11Y FontSizing] Constructor - maxFontSize:', this.maxFontSize);
        }
    }

    enable() {
        super.enable();
        if (this.mode === 'readableFont') {
            this.addBodyClass('dhA11y-readable-font');
        } else {
            // Restore saved values
            const saved = this.widget.storage.get('font_sizing_values');
            if (saved) {
                this.currentSize = saved.fontSize || 100;
                this.currentLineSpacing = saved.lineSpacing || 0;
                this.currentWordSpacing = saved.wordSpacing || 0;
                this.currentLetterSpacing = saved.letterSpacing || 0;
            }
            this._scanElements();
            this._apply();
        }
    }

    disable() {
        super.disable();
        if (this.mode === 'readableFont') {
            this.removeBodyClass('dhA11y-readable-font');
        } else {
            this._reset();
        }
    }

    /**
     * Increase font size
     */
    increaseFontSize() {
        if (this.currentSize < this.settings.max) {
            this.currentSize += this.settings.step;
            this._applyPreset(this.currentSize);
            this._apply();
            this._save();
            this.widget.events.emit('fontSizing:change', { key: 'fontSize', value: this.currentSize });
        }
    }

    /**
     * Decrease font size
     */
    decreaseFontSize() {
        if (this.currentSize > this.settings.min) {
            this.currentSize -= this.settings.step;
            
            // Special case: when reaching 100%, disable feature (same as .reset-text)
            if (this.currentSize === 100) {
                this.widget.toggleFeature('fontSizing', false);
                return;
            }
            
            this._applyPreset(this.currentSize);
            this._apply();
            this._save();
            this.widget.events.emit('fontSizing:change', { key: 'fontSize', value: this.currentSize });
        }
    }

    /**
     * Increase line spacing
     */
    increaseLineSpacing() {
        if (this.currentLineSpacing < 10) {
            this.currentLineSpacing++;
            this._apply();
            this._save();
        }
    }

    /**
     * Decrease line spacing
     */
    decreaseLineSpacing() {
        if (this.currentLineSpacing > 0) {
            this.currentLineSpacing--;
            this._apply();
            this._save();
        }
    }

    /**
     * Increase word spacing
     */
    increaseWordSpacing() {
        if (this.currentWordSpacing < 10) {
            this.currentWordSpacing++;
            this._apply();
            this._save();
        }
    }

    /**
     * Decrease word spacing
     */
    decreaseWordSpacing() {
        if (this.currentWordSpacing > 0) {
            this.currentWordSpacing--;
            this._apply();
            this._save();
        }
    }

    /**
     * Increase letter spacing
     */
    increaseLetterSpacing() {
        if (this.currentLetterSpacing < 10) {
            this.currentLetterSpacing++;
            this._apply();
            this._save();
        }
    }

    /**
     * Decrease letter spacing
     */
    decreaseLetterSpacing() {
        if (this.currentLetterSpacing > 0) {
            this.currentLetterSpacing--;
            this._apply();
            this._save();
        }
    }

    /**
     * Scan all text elements and store their original computed styles
     */
    _scanElements() {
        if (this._scanned) return;
        this._elements = [];

        const container = document.body;
        const selector = this._tags.join(',');
        const allEls = container.querySelectorAll(selector);

        allEls.forEach(el => {
            // Skip elements inside ignored containers
            if (el.closest(this._ignoreSelectors)) return;

            const computed = window.getComputedStyle(el);
            const fontSize = parseFloat(computed.fontSize);
            const lineHeight = parseFloat(computed.lineHeight);
            const wordSpacing = parseFloat(computed.wordSpacing);
            const letterSpacing = parseFloat(computed.letterSpacing);

            if (isNaN(fontSize) || fontSize === 0) return;

            this._elements.push({
                el: el,
                originalFontSize: fontSize,
                originalLineHeight: isNaN(lineHeight) ? fontSize * 1.5 : lineHeight,
                originalWordSpacing: isNaN(wordSpacing) ? 0 : wordSpacing,
                originalLetterSpacing: isNaN(letterSpacing) ? 0 : letterSpacing,
                originalInlineStyle: el.getAttribute('style') || ''
            });

            // Mark element as scanned
            el.classList.add('dhA11y-text');
        });

        this._scanned = true;
    }

    /**
     * Apply preset values for current font size level
     */
    _applyPreset(fontSize) {
        // Special case: fontSize 100 = reset all spacing to 0
        if (fontSize === 100) {
            this.currentLineSpacing = 0;
            this.currentLetterSpacing = 0;
            this.currentWordSpacing = 0;
            return;
        }

        // Apply preset if available
        const preset = this.presets[fontSize];
        if (preset) {
            this.currentLineSpacing = preset.lineSpacing || 0;
            this.currentLetterSpacing = preset.letterSpacing || 0;
            this.currentWordSpacing = preset.wordSpacing || 0;
            
            if (this.widget.config.debug) {
                console.log('[A11Y FontSizing] Applied preset for size', fontSize, ':', preset);
            }
        } else if (this.widget.config.debug) {
            console.warn('[A11Y FontSizing] No preset found for size', fontSize);
        }
    }

    /**
     * Apply current values per-element (like EasyView / EqualWeb)
     */
    _apply() {
        if (!this._scanned) this._scanElements();

        const ratio = this.currentSize / 100;

        // Update body classes for current font size
        this._updateBodyClasses();

        this._elements.forEach(item => {
            const el = item.el;

            // Font size: apply ratio to original computed size with maxFontSize cap
            if (this.currentSize !== 100) {
                let newSize = item.originalFontSize * ratio;
                if (newSize > this.maxFontSize) {
                    newSize = this.maxFontSize;
                }
                el.style.setProperty('font-size', newSize.toFixed(1) + 'px', 'important');
            } else {
                el.style.removeProperty('font-size');
            }

            // Line spacing: increase by factor of original line-height
            // Skip adjustment for button/a elements with 44px line-height to prevent excessive spacing
            const isButtonOrLink = el.tagName === 'BUTTON' || el.tagName === 'A';
            const isFixedHeight = Math.abs(item.originalLineHeight - 44) < 1;
            const shouldSkipLineSpacing = isButtonOrLink && isFixedHeight;
            
            if (this.currentLineSpacing > 0 && !shouldSkipLineSpacing) {
                const factor = 1 + (this.currentLineSpacing * 0.15);
                const newLH = item.originalLineHeight * factor;
                el.style.setProperty('line-height', newLH.toFixed(1) + 'px', 'important');
            } else {
                el.style.removeProperty('line-height');
            }

            // Word spacing: add px offset
            if (this.currentWordSpacing > 0) {
                const newWS = item.originalWordSpacing + (this.currentWordSpacing * 2);
                el.style.setProperty('word-spacing', newWS.toFixed(1) + 'px', 'important');
            } else {
                el.style.removeProperty('word-spacing');
            }

            // Letter spacing: add px offset
            if (this.currentLetterSpacing > 0) {
                const newLS = item.originalLetterSpacing + (this.currentLetterSpacing * 0.5);
                el.style.setProperty('letter-spacing', newLS.toFixed(1) + 'px', 'important');
            } else {
                el.style.removeProperty('letter-spacing');
            }

            // Word break: apply to prevent layout overflow
            if (this.currentSize !== 100) {
                el.style.setProperty('word-break', this.wordBreak, 'important');
                el.style.setProperty('hyphens', 'auto', 'important');
            } else {
                el.style.removeProperty('word-break');
                el.style.removeProperty('hyphens');
            }
        });

        // Sync heading line-heights with their triangle ::after spans
        // to prevent white space gaps at different font sizes/resolutions
        if (this.currentSize !== 100) {
            this._syncTriangleLineHeights();
        }

        // Adjust triangle right offset for sub-pixel rounding at >= 110%
        if (this.currentSize >= 110) {
            const scaledRight = getComputedStyle(document.documentElement)
                .getPropertyValue('--triangle-right-scaled').trim();
            if (scaledRight) {
                document.documentElement.style.setProperty('--triangle-right', scaledRight);
            }
            // Remove span margin-bottom to prevent white space below triangle
            document.documentElement.style.setProperty('--triangle-span-margin-bottom', '0px');
        } else {
            document.documentElement.style.removeProperty('--triangle-right');
            document.documentElement.style.removeProperty('--triangle-span-margin-bottom');
        }

        // document.body.classList.add('dhA11y-font-sizing-active'); // Moved to _updateBodyClasses()
    }

    /**
     * Sync line-height of parent elements with their child spans that have
     * clip-path triangles (::after or ::before) to prevent white space gaps
     */
    _syncTriangleLineHeights() {
        const candidates = document.querySelectorAll(
            'h1 > span, .h1 > span, h2 > span, .h2 > span, ' +
            '[data-original-level] > span, [data-converted-from] > span, ' +
            '.nav-tabs > li > a > span'
        );
        candidates.forEach(span => {
            const after = window.getComputedStyle(span, '::after');
            const before = window.getComputedStyle(span, '::before');
            const hasClipPath = (after.clipPath && after.clipPath !== 'none') ||
                               (before.clipPath && before.clipPath !== 'none');
            if (hasClipPath) {
                const spanLH = window.getComputedStyle(span).lineHeight;
                const parent = span.parentElement;
                if (parent && parent !== document.body) {
                    parent.style.setProperty('line-height', spanLH, 'important');
                }
            }
        });
    }
    _updateBodyClasses() {
        const body = document.body;
        
        // Remove existing font sizing classes
        const existingClasses = Array.from(body.classList).filter(className => 
            className.startsWith('dhA11y-font-sizing-')
        );
        existingClasses.forEach(className => {
            body.classList.remove(className);
        });
        
        // Add active class and current size class
        body.classList.add('dhA11y-font-sizing-active');
        body.classList.add(`dhA11y-font-sizing-${this.currentSize}`);
    }

    /**
     * Reset all values and restore original inline styles
     */
    _reset() {
        this.currentSize = 100;
        this.currentLineSpacing = 0;
        this.currentWordSpacing = 0;
        this.currentLetterSpacing = 0;

        this._elements.forEach(item => {
            const el = item.el;
            // Remove all font sizing related properties
            el.style.removeProperty('font-size');
            el.style.removeProperty('line-height');
            el.style.removeProperty('word-spacing');
            el.style.removeProperty('letter-spacing');
            el.style.removeProperty('word-break');
            el.style.removeProperty('hyphens');
            
            // Restore original inline style if it exists
            if (item.originalInlineStyle) {
                el.setAttribute('style', item.originalInlineStyle);
            } else {
                el.removeAttribute('style');
            }
            el.classList.remove('dhA11y-text');
        });

        this._elements = [];
        this._scanned = false;

        // Remove all font sizing related body classes
        const body = document.body;
        const existingClasses = Array.from(body.classList).filter(className => 
            className.startsWith('dhA11y-font-sizing-')
        );
        existingClasses.forEach(className => {
            body.classList.remove(className);
        });

        // Remove triangle right offset override
        document.documentElement.style.removeProperty('--triangle-right');
        document.documentElement.style.removeProperty('--triangle-span-margin-bottom');

        // document.body.classList.remove('dhA11y-font-sizing-active'); // Replaced with above
        this.widget.storage.remove('font_sizing_values');
    }

    /**
     * Save current values
     */
    _save() {
        this.widget.storage.set('font_sizing_values', {
            fontSize: this.currentSize,
            lineSpacing: this.currentLineSpacing,
            wordSpacing: this.currentWordSpacing,
            letterSpacing: this.currentLetterSpacing
        });
    }
}
